
import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';
import { useNavigate } from 'react-router-dom';

const InstructorPayoutSettingsPage = () => {
    const { user, fetchProfile } = useAuthStore();
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        accountNumber: '',
        accountName: '',
        bankCode: ''
    });
    const [banks, setBanks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const res = await axiosClient.get('https://api.paystack.co/bank', {
                    headers: { Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_PUBLIC_KEY}` }
                });
                setBanks(res.data.data);
            } catch (err) {
                toast.error('Failed to load banks');
            }
        };
        fetchBanks();
    }, [toast]);

    useEffect(() => {
        if (user?.bankAccount) {
            setFormData({
                accountNumber: user.bankAccount.accountNumber || '',
                accountName: user.bankAccount.accountName || '',
                bankCode: user.bankAccount.bankCode || ''
            });
        }
    }, [user]);

    const handleVerifyAccount = async () => {
        if (!formData.accountNumber || !formData.bankCode) {
            return toast.error('Please enter account number and select bank');
        }
        setIsVerifying(true);
        try {
            const resolveRes = await axiosClient.post('/payments/resolve-account', {
                account_number: formData.accountNumber,
                bank_code: formData.bankCode
            });
            setFormData(prev => ({ ...prev, accountName: resolveRes.data.data.account_name }));
            toast.success('Account verified successfully!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to verify account');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.accountName) {
            return toast.error('Please verify your account first');
        }

        setIsLoading(true);
        try {

            const recipientRes = await axiosClient.post('/payments/create-recipient', {
                type: 'nuban',
                name: formData.accountName,
                account_number: formData.accountNumber,
                bank_code: formData.bankCode,
                currency: 'NGN'
            });

            await axiosClient.put('/users/profile/payout', {
                bankAccount: {
                    accountNumber: formData.accountNumber,
                    accountName: formData.accountName,
                    bankCode: formData.bankCode,
                    bankName: banks.find(b => b.code === formData.bankCode)?.name || 'Unknown Bank'
                },
                paystackRecipientCode: recipientRes.data.data.recipient_code,
                isPaystackVerified: recipientRes.data.data.active
            });

            fetchProfile()
            toast.success('Payout account saved successfully!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save payout account');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <DashboardLayout userRole="instructor">
            <div className="max-w-2xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <button
                        onClick={() => navigate(`/instructor/revenue`)}
                        className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to revenue
                    </button>

                </div>
                <h1 className="text-2xl font-bold mb-6 dark:text-neutral-300">Payout Settings</h1>
                <p className="text-neutral-600 mb-8 dark:text-neutral-300">
                    Add your Nigerian bank account to receive course revenue payouts.
                    <br />
                    <span className="text-sm text-red-500">
                        * Account name must match your full instructor profile name ({user.name})
                    </span>
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium mb-2 dark:text-neutral-300">Account Number</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={formData.accountNumber}
                                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                                className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                placeholder="0123456789"
                                required
                            />
                            <button
                                type="button"
                                onClick={handleVerifyAccount}
                                disabled={isVerifying || !formData.accountNumber || !formData.bankCode}
                                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50"
                            >
                                {isVerifying ? 'Verifying...' : 'Verify Account'}
                            </button>
                        </div>
                    </div>

                    {formData.accountName && (
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <p className="text-green-700 dark:text-green-300">
                                ✅ Account name: <strong>{formData.accountName}</strong>
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-2 dark:text-neutral-300">Bank</label>
                        <select
                            value={formData.bankCode}
                            onChange={(e) => setFormData({ ...formData, bankCode: e.target.value })}
                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Select Bank</option>
                            {banks.map(bank => (
                                <option key={bank.code} value={bank.code}>
                                    {bank.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading || !formData.accountName}
                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50"
                    >
                        {isLoading ? 'Saving...' : 'Save Payout Account'}
                    </button>
                </form>
            </div>
        </DashboardLayout>
    );
};

export default InstructorPayoutSettingsPage;